import { openProductQuickView } from "@/components/productQuickView/store"
import { Badge } from "@/components/ui/Badge"
import { Button } from "@/components/ui/Button"
import { flagForCurrency } from "@/components/ui/Flag"
import { TableCell, TableRow } from "@/components/ui/Table"
import { shortDate, units } from "@/lib/format"
import type { Market } from "../lib/types"
import { impactText, isLate, type IncomingOrderLine } from "../lib/incoming"

const DASH = <span className="text-muted-foreground">–</span>
const CELL = "px-2 py-1.5 align-top"

interface Props {
  line: IncomingOrderLine
  market: Market | undefined
  today: string
}

// One order line, one table row. The product opens the product quick view on
// the row's market.
export default function IncomingOrderRow({ line, market, today }: Props) {
  const Flag = flagForCurrency(market?.currency)
  const impact = impactText(line.impact, market?.name, today)

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className={`${CELL} whitespace-nowrap font-medium`}>{line.order_no ?? DASH}</TableCell>
      <TableCell className={`${CELL} max-w-72`}>
        <Button
          variant="link"
          className="h-auto max-w-full justify-start p-0 text-left text-sm font-normal whitespace-normal"
          title={[line.name, line.sku].filter(Boolean).join(" · ")}
          onClick={() => openProductQuickView(line.product_external_id, market ?? null)}
        >
          <span className="tabular-nums">{line.product_external_id}</span>
          <span className="ml-2 line-clamp-1">{line.name ?? "Unknown product"}</span>
        </Button>
      </TableCell>
      <TableCell className={`${CELL} whitespace-nowrap`}>
        <div className="flex items-center gap-2">
          {Flag && <Flag />}
          {market?.name ?? "Unknown"}
          {line.how === "assumed" && (
            <span
              className="inline-block h-1.5 w-1.5 rounded-full bg-warning"
              title="Destination assumed: no country or warehouse matched, so it was sent to the default market."
            />
          )}
        </div>
      </TableCell>
      <TableCell className={`${CELL} text-right tabular-nums`}>{units(line.quantity)}</TableCell>
      <TableCell className={`${CELL} whitespace-nowrap tabular-nums`}>
        {line.etd ? shortDate(line.etd, today) : DASH}
      </TableCell>
      <TableCell className={`${CELL} whitespace-nowrap tabular-nums`}>
        <div className="flex items-center gap-2">
          {line.arrives_on ? shortDate(line.arrives_on, today) : "No ETA"}
          {isLate(line, today) && <Badge variant="danger">Late</Badge>}
        </div>
      </TableCell>
      <TableCell className={CELL}>{line.status}</TableCell>
      <TableCell className={`${CELL} max-w-56`}>{line.warehouse_note ?? DASH}</TableCell>
      <TableCell className={`${CELL} ${line.impact.kind === "runs_out_before" ? "text-destructive" : ""}`}>
        {impact}
      </TableCell>
    </TableRow>
  )
}
